import {
  getChildMoimGroup,
  getChildMoimGroupMoims,
  getComponentLayout,
  updateProfile,
} from "@/api/sugGroupDialog";
import AlertDialog from "@/common/components/DialogBase/alertDialog";
import { HelperText } from "@/common/components/section/styled";
import { GROUP_ID } from "@/constant/group";
import { Wrapper } from "@/page/devProfileSetting/styled";
import BlockitRendererV2 from "@/page/profile/components/blockitRenderer.v2";
import {
  ScopedSignUpSubgroupSelect,
  ScopedBlockitRendererContextProvider,
} from "@moim/blockit";
import { MoimOpener, px2rem, SkeletonBox, useIntlShort } from "@moim/common";
import axios, { isAxiosError } from "axios";
import { ThemeContext } from "styled-components";
import React, { useContext, useState } from "react";
import { Buttons } from "./components/buttons";
import { Contents, ScrollSection } from "./components/styled";

export const Subgroup: React.FC<{
  childMoimGroupIds?: string[];
  maxCount?: number;
  minCount?: number;
  onNext(): void;
}> = ({ childMoimGroupIds, maxCount, minCount, onNext }) => {
  const intl = useIntlShort();
  const isOptional = false;
  const [api, dom] = MoimOpener.useOpener();
  // const { dispatchGetComponentLayout } = useActions({
  //   dispatchGetComponentLayout: getComponentLayout,
  // });
  const theme = useContext(ThemeContext);
  const currentGroupId = GROUP_ID;
  // const { key, fallbackKey } = getLayoutKey(
  //   currentGroupId,
  //   "signup-join-subgroup",
  // );

  const [layout, setLayout] = useState<Moim.Component.IComponentLayout | null>(
    null,
  );

  // const layout = useStoreState(state =>
  //   state.componentLayout?.layout?.[key ?? ""]?.blocks?.length
  //     ? state.componentLayout?.layout?.[key ?? ""]
  //     : state.componentLayout?.layout?.[fallbackKey ?? ""],
  // );

  const [groups, setGroups] = React.useState<
    {
      childMoimGroup: Moim.ChildMoimGroup.IChildMoimGroupData;
      moims: Moim.ChildMoimGroup.IChildMoimGroupMoimRawDatum[];
    }[]
  >();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [subgroups, setSubgroups] = React.useState<
    { id: string; name: string }[]
  >([]);
  const [error, setError] = React.useState<string>();
  // const { dispatchUpdateMyProfile } = useActions({
  //   dispatchUpdateMyProfile: updateMyProfile,
  // });

  const handleClickDoneButton = React.useCallback(async () => {
    if (maxCount !== undefined && subgroups?.length > maxCount) {
      setError(
        intl("error_options_max_count", {
          max_count: maxCount,
        }),
      );
      return;
    } else if (minCount !== undefined && subgroups?.length < minCount) {
      setError(
        intl("error_options_min_count", {
          min_count: minCount,
        }),
      );
      return;
    } else {
      setError(undefined);
    }

    if (!GROUP_ID) {
      return;
    }

    const handleConfirm = async () => {
      try {
        setIsLoading(true);

        const updateParams = {
          signUpInfo: {
            subgroupIds: subgroups.map(v => v.id),
            finishedStep: "subgroup" as const,
          },
        };

        const { success, err } = await (async () => {
          try {
            await updateProfile(updateParams, undefined);
            return { success: true };
          } catch (err) {
            if (axios.isAxiosError(err)) {
              return { success: false, err: errorParseData(err) };
            }
            return { success: false };
          }
        })();

        if (success) {
          onNext();
        } else {
          setError(err?.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (layout?.confirm && layout.confirm.enabled) {
      api.open(({ close }) => (
        <AlertDialog
          open={true}
          title={layout.confirm?.dialog?.title?.replace(
            "{{ groupNames }}",
            subgroups.map(s => s.name).join(", "),
          )}
          titleLocale={layout.confirm?.dialog?.titleLocale?.map(l => ({
            locale: l.locale,
            value: l.value.replace(
              "{{ groupNames }}",
              subgroups.map(s => s.name).join(", "),
            ),
          }))}
          content={layout.confirm?.dialog?.description?.replace(
            "{{ groupNames }}",
            subgroups.map(s => s.name).join(", "),
          )}
          contentLocale={layout.confirm?.dialog?.descriptionLocale?.map(l => ({
            locale: l.locale,
            value: l.value.replace(
              "{{ groupNames }}",
              subgroups.map(s => s.name).join(", "),
            ),
          }))}
          rightButtons={[
            {
              text: layout.confirm?.dialog?.cancelButton,
              textLocale: layout.confirm?.dialog?.cancelButtonLocale,
              textColor: theme.colorV2.colorSet.grey300,
              onClick: close,
            },
            {
              text: layout.confirm?.dialog?.okButton,
              textLocale: layout.confirm?.dialog?.okButtonLocale,
              textColor: theme.colorV2.colorSet.grey1000,
              onClick: () => {
                close();
                handleConfirm();
              },
            },
          ]}
          onClose={close}
        />
      ));
    } else {
      handleConfirm();
    }
  }, [subgroups, onNext, theme, layout]);

  React.useEffect(() => {
    if (currentGroupId) {
      getComponentLayout(currentGroupId, "signup-join-subgroup").then(
        result => {
          // state.componentLayout?.layout?.[key ?? ""]?.blocks?.length
          //     ? state.componentLayout?.layout?.[key ?? ""]
          //     : state.componentLayout?.layout?.[fallbackKey ?? ""],
          setLayout(result.data);
        },
      );
    }
  }, [currentGroupId]);

  React.useEffect(() => {
    if (childMoimGroupIds?.length) {
      Promise.all(
        childMoimGroupIds.map(async id => {
          const childMoimGroup = await getChildMoimGroup(id);
          const moims = await getChildMoimGroupMoims(id, 20);

          return {
            childMoimGroup: childMoimGroup.data,
            moims: moims.data,
          };
        }),
      ).then(result => {
        setGroups([...result]);
      });
    }
  }, [childMoimGroupIds]);

  const handleSingleSelect = React.useCallback(
    (value: string, name: string) => {
      const isSelected = subgroups?.[0]?.id === value;
      if (isSelected) {
        setSubgroups([]);
      } else {
        setSubgroups([{ id: value, name }]);
      }
    },
    [subgroups],
  );

  const handleMultiSelect = React.useCallback(
    (value: string, name: string) => {
      const isSelected = subgroups?.map(v => v.id).includes(value);
      if (isSelected) {
        setSubgroups(
          subgroups?.length > 1 ? subgroups?.filter(v => v.id !== value) : [],
        );
      } else {
        setSubgroups([...subgroups, { id: value, name }]);
      }
    },
    [subgroups],
  );

  React.useCallback(() => {
    // fetchProfile();
  }, [groups]);
  return (
    <Wrapper>
      <Contents>
        <ScrollSection>
          <ScopedBlockitRendererContextProvider
            renderScopedBlock={block => {
              switch (block.subType) {
                case "signup|select-subgroup":
                  return (
                    <ScopedSignUpSubgroupSelect
                      groups={groups}
                      selected={subgroups.map(v => v.id)}
                      onSelect={
                        maxCount === 1 ? handleSingleSelect : handleMultiSelect
                      }
                    />
                  );

                default:
                  return null;
              }
            }}
          >
            {layout ? (
              layout.blocks?.map((block, idx) => (
                <BlockitRendererV2 key={`${block.type}_${idx}`} block={block} />
              ))
            ) : (
              <SkeletonBox width="100%" height={px2rem(200)} />
            )}
          </ScopedBlockitRendererContextProvider>
        </ScrollSection>

        {error && <HelperText state={"error"}>{error}</HelperText>}
        <Buttons
          buttonText={layout?.confirm?.dialog?.okButton}
          onClick={handleClickDoneButton}
          waitingButton={isLoading}
          disabledButton={(!isOptional && subgroups.length === 0) || !layout}
        />
      </Contents>
      {dom}
    </Wrapper>
  );
};

function errorParseData(
  rawError: Error | unknown,
): Moim.IErrorResponse | undefined {
  if (isAxiosError(rawError)) {
    const response = rawError.response;

    if (response) {
      const isVingleServerError = response.data && response.data.error;
      if (isVingleServerError) {
        return response.data.error;
      } else if (response.data.message) {
        return response.data;
      }
    }
  }
}
